import React, { useMemo } from 'react';
import * as ReactVirtualized from 'react-virtualized';

import {
  styled,
  LegacyText,
  Tooltip,
  LegacyBox,
  IconButton,
  FileCopyIcon,
  Divider,
} from '@gogame/components';
import { useMountedState, useCopyToClipboard } from '@gogame/hooks-ts';
import { useScreenMessage } from '@gogame/screens';

import SearchField from './SearchField';
import { MessageBox } from './components';

const ListToolbar = styled(LegacyBox)`
  padding: 8px 16px;
`;

export default ({ items }) => {
  const [searchQuery, setSearchQuery] = useMountedState(0);
  const { showSuccessMessage } = useScreenMessage();
  const copyToClipboard = useCopyToClipboard();
  const filteredItems = useMemo(() => {
    let result = [...items];
    if (searchQuery) {
      return result.filter((metric) => {
        return (
          (metric.name || '')
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (metric.description || '')
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
        );
      });
    }
    return result;
  }, [items, searchQuery]);

  const handleCopy = (name) => {
    showSuccessMessage(`"${name}" Copied to clipboard`);
    copyToClipboard(name);
  };

  const rowRenderer = ({ index, style }) => {
    const { name, description } = filteredItems[index];
    return (
      <MessageBox key={index} gutter={4} align={'center'} style={style}>
        <IconButton
          icon={<FileCopyIcon />}
          tooltip={'Copy name'}
          onClick={() => handleCopy(name)}
        />
        <LegacyBox vertical={true} style={{ minWidth: '0' }}>
          <LegacyText>{name}</LegacyText>
          <Tooltip title={description} wrapper>
            <LegacyText variant={'caption'} type={'draft'} ellipsis={true}>
              {description}
            </LegacyText>
          </Tooltip>
        </LegacyBox>
      </MessageBox>
    );
  };

  return (
    <LegacyBox vertical={true} grow={1} width={'65%'}>
      <LegacyBox vertical={true}>
        <ListToolbar
          vertical={true}
          align={'stretch'}
          justifyContent={'center'}
        >
          <SearchField onChange={setSearchQuery} />
        </ListToolbar>
        <Divider />
      </LegacyBox>

      <LegacyBox
        vertical={true}
        style={{ overflow: 'auto', paddingBottom: '8px', flexGrow: 1 }}
      >
        <ReactVirtualized.AutoSizer disableWidth={false}>
          {({ height, width }) => {
            return (
              <ReactVirtualized.List
                style={{ outline: 'none', padding: '8px 0' }}
                width={width}
                height={height}
                rowCount={filteredItems.length}
                rowHeight={48}
                rowRenderer={rowRenderer}
              />
            );
          }}
        </ReactVirtualized.AutoSizer>
      </LegacyBox>
    </LegacyBox>
  );
};
